import { Component, Input, ViewChild } from '@angular/core'
import { BehaviorSubject, Observable, catchError, combineLatest, filter, finalize, map, of, take, tap } from 'rxjs'
import {
   PIMSProduct,
   AdminProductsClient,
   AdminRequestDto,
   ProductMapNoteDto,
   VCLinkProductRequest,
   ProductsClient,
   VetCorProduct,
   ApplicationArea,
} from 'src/app/shared/services/ppv.api'
import { ProductsService } from '../products.service'
import { ToastrService } from 'ngx-toastr'
import { PIMSProductUIModel } from '../models/models'
import { TreeNode } from 'primeng/api'
import { ProductFunctions } from '../functions'
import { Table } from 'primeng/table'
import { Sort } from '@angular/material/sort'
import { SortEvent } from 'primeng/api'
import * as XLSX from 'xlsx'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import * as Papa from 'papaparse'

@Component({
   selector: 'vc-product-pims-list',
   templateUrl: './product-pims-list.component.html',
   styleUrls: ['./product-pims-list.component.scss'],
   standalone: false,
})
export class ProductPimsListComponent {
   _rows: PIMSProductUIModel[] = []
   @Input() vcProductSKey: number
   @Input() siteCount: number
   @Input() showPracticeColumn = true
   @Input() showPriceColumn = false
   @Input() vcProductsWithoutMappedPercentage: boolean = false
   @Input() vcProductsWithCodes: boolean = false
   @Input() set rows(value: PIMSProductUIModel[]) {
      this._rows = value.map(p => ({
         ...p,
         hospitalName: p.practice?.nameId
      }));
      this.calculateCounts();
   }
   get rows(): PIMSProductUIModel[] {
      return this._rows
   }
   @Input() virtualScrollItemSize: number | undefined = undefined
   @Input() pimsProductEditable: boolean = false
   @ViewChild('dt') private table: Table

   allVCProducts$ = this.productService.allVCProducts$
   hierarchicalProducts$: Observable<TreeNode<any>[]>

   virtualScrollProps = {}
   rowStyle = ''
   unlinkDialogShown = false
   selectOptions = [
      { label: 'Yes', value: true },
      { label: 'No', value: false },
   ]
   isThisCodeUsed = undefined
   currentProduct = undefined
   missingProduct = undefined
   triedSearching = undefined
   reasonOfAdd = undefined
   counts = { verified: 0, newCodeReq: 0, disableReq: 0, actionNeeded: 0 }

   sameMapDialogShown = false
   reasonSameMapping = undefined
   selectedVcCategory = ''
   selectedVcCategoryId = 0
   selectedVcProduct = ''
   selectedVcId = 0

   isMultiSortEnabled = false
   sortField: string | null = null
   sortOrder: number = 1

   sameMapCounts: Map<number, string[]> = new Map<number, string[]>()
   productNotesCountMap: Map<number, number> = new Map()
   productMapNotes: ProductMapNoteDto[] = []
   loadingMapNotes: boolean = false
   showCommentBox: boolean = false
   showCommentLink: boolean = true
   selectedNoteNote = ''
   selectedNoteDate = ''
   selectedNoteAction = 0
   canDoSameMap: boolean = true // change to true for 9-30 commit
   userFeedback = ''
   selectedNoteProductSKey = 0
   multiSortMeta: any[] = []
   firstClickedColumn: string | null = null
   secondClickedColumn: string | null = null
   clickedProdSKey: string

   displayModal: boolean = false
   csvData: any = null
   batchCSCLoading = false
   csvUploaded = false
   batchSuccessMessage: string | null = null
   isUpdateFinished: boolean = false
   public canAccessProducts = false
   batchErrorMessages: string[] = []

   constructor(
      private productService: ProductsService,
      private adminProductsClient: AdminProductsClient,
      private api: ProductsClient,
      private toastr: ToastrService,
      private authMgr: AuthManager
   ) {}

   ngOnInit() {
      this.multiSortMeta = []

      this.hierarchicalProducts$ = this.vcProductsWithCodes
         ? this.productService.hierarchicalProductsWithCodes$
         : this.vcProductsWithoutMappedPercentage
         ? this.productService.hierarchicalProductsWithoutMappedPercentage$
         : this.productService.hierarchicalProducts$
      this.productService.loadVCProducts()

      const productSKeys = this._rows.map((product) => product.productSKey)
      this.fetchNotesCounts(productSKeys)

      const vcProductSKeys = this._rows.map((product) => ({ vcProductSKey: product.vcProductSKey, practiceSKey: product.practiceSKey }))
      this.fetchSameMappedCounts(vcProductSKeys)

      this.authMgr.getCurrentUser().subscribe((user) => {
         this.canAccessProducts = this.authMgr.canAccess(user, {
            application: ApplicationArea.Products,
            roleName: ['VCProduct'],
         })
      })
   }

   showModal() {
      this.displayModal = true
      this.isUpdateFinished = false
   }

   handleCloseButton() {
      if (this.isUpdateFinished) {
         window.location.reload()
      } else {
         this.displayModal = false
      }
   }

   onFileChange(event: any): void {
      const file: File = event.target.files[0]
      if (file) {
         this.parseCSVData(file)
         this.csvUploaded = true
      }
   }

   parseCSVData(csvData: File): void {
      const batchErrorMessages: string[] = []
      Papa.parse(csvData, {
         header: true,
         skipEmptyLines: true,
         dynamicTyping: true,
         complete: (result) => {
            //this.csvData = result.data;
            //console.log('Parsed CSV:', result);
            this.csvData = result.data.filter((row) => Object.values(row).some((value) => value !== null && value !== '' && value !== ' '))

            if (result.errors && result.errors.length > 0) {
               result.errors.forEach((error) => {
                  if (!this.batchErrorMessages.includes('Row ' + error.row + ': ' + error.message)) {
                     this.batchErrorMessages.push('Row ' + error.row + ': ' + error.message)
                  }
               })
            }
         },
         error: (error) => {
            console.error('Error parsing CSV:', error)
         },
      })
   }

   updateProducts() {
      this.batchCSCLoading = true
      this.batchSuccessMessage = null
      this.batchErrorMessages = []

      try {
         this.api.batchUpdateProduct(false, this.csvData).subscribe({
            next: (response) => {
               this.batchCSCLoading = false
               this.isUpdateFinished = true
               this.batchSuccessMessage = response
               this.csvUploaded = false
            },
            error: (error) => {
               this.batchCSCLoading = false
               let parsed
               parsed = JSON.parse(error.response)
               if (parsed?.errors) {
                  for (const key in parsed.errors) {
                     if (parsed.errors.hasOwnProperty(key)) {
                        const messages = parsed.errors[key]
                        if (Array.isArray(messages)) {
                           const match = key.match(/\[(\d+)\]/)
                           const rowNum = match ? parseInt(match[1], 10) + 2 : key
                           this.batchErrorMessages.push(`Row ${rowNum}: ${messages.join(', ')}`)
                        }
                     }
                  }
               }
               this.batchErrorMessages.push('')
               this.batchErrorMessages.push('Please correct the CSV and upload your file again.')
            },
         })
      } catch (err: any) {
         this.batchCSCLoading = false
         this.batchErrorMessages.push('A fatal error occurred during the batch update.')
         console.error(err)
      }
   }

   handleSort(field: string) {
      if (this.firstClickedColumn != null && this.secondClickedColumn === null) {
         if (field != this.firstClickedColumn) {
            this.secondClickedColumn = field
         }
      }

      if (this.firstClickedColumn === null) {
         this.firstClickedColumn = field
      }

      if (field != this.firstClickedColumn && field != this.secondClickedColumn) {
         this.multiSortMeta = []
         this.firstClickedColumn = field
         this.secondClickedColumn = null
      }

      const existingMeta = this.multiSortMeta.find((meta) => meta.field === field)
      if (existingMeta) {
         // Toggle sort order if the column is already being sorted
         existingMeta.order = existingMeta.order === 1 ? -1 : 1
      } else {
         // Add new sort order for the column
         this.multiSortMeta.push({ field, order: 1 })
      }
      this.onSort()
   }

   onSort() {
      if (this.isMultiSortEnabled) {
         if (this.multiSortMeta.length === 0) {
            //console.warn('No sorting metadata found.');
            return
         }

         // Implement custom sort logic here based on multiSortMeta
         this._rows.sort((a, b) => {
            for (let i = 0; i < this.multiSortMeta.length; i++) {
               const meta = this.multiSortMeta[i]
               const field = meta.field
               const order = meta.order

               const valueA = a[field]
               const valueB = b[field]

               let comparison = 0

               if (valueA == null && valueB != null) {
                  comparison = -1
               } else if (valueA != null && valueB == null) {
                  comparison = 1
               } else if (valueA == null && valueB == null) {
                  comparison = 0
               } else if (typeof valueA === 'string' && typeof valueB === 'string') {
                  comparison = valueA.localeCompare(valueB)
               } else {
                  comparison = valueA < valueB ? -1 : valueA > valueB ? 1 : 0
               }

               if (comparison !== 0) {
                  return comparison * order
               }
            }
            return 0
         })
      } else {
      }
   }

   singleSort(field: string) {
      if (this.sortField === field) {
         // If already sorted by this field, toggle the sort order
         this.sortOrder = this.sortOrder === 1 ? -1 : 1
      } else {
         // If sorting by a new field, reset the order to ascending
         this.sortOrder = 1
         this.sortField = field
      }

      // Sort the rows array based on the field and order
      this._rows.sort((a, b) => {
         const valueA = a[field]
         const valueB = b[field]

         let comparison = 0

         // Handling null/undefined values
         if (valueA == null && valueB != null) {
            comparison = -1
         } else if (valueA != null && valueB == null) {
            comparison = 1
         } else if (valueA == null && valueB == null) {
            comparison = 0
         } else {
            comparison = valueA < valueB ? -1 : valueA > valueB ? 1 : 0
         }

         // Apply sort order (ascending or descending)
         return comparison * this.sortOrder
      })
   }

   onExportToExcel() {
      const ws = XLSX.utils.book_new()
      var data = this._rows.map((d) => {
         return {
            Code: d.code,
			'VC Code': d.vcProductSKey == 0 || d.vcProductSKey == -1 ? '' : 'V0' + d.vcProductSKey,			
            'Your Category': d.categoryName,
            'Your Product Name': d.name,
            Type: d.productType == 1 ? 'Product' : 'Service',
            Price: d.price,
            'VC Product': d.vcProduct?.name,
            Status: d.mappingScore == 100 ? 'Verified' : d.notNeeded ? 'DisableRequest' : !!d.reasonOfAdd ? 'NewCodeRequest' : 'ActionNeeded',
         }
      })
      XLSX.utils.sheet_add_json(ws, data)
      const wb = { Sheets: { data: ws }, SheetNames: ['data'] }
      XLSX.writeFile(wb, 'export.xlsx', { bookType: 'xlsx', type: 'array' })
      //const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
   }

   calculateCounts() {
      this.counts.verified = this._rows.filter((r) => r.mappingScore == 100).length
      this.counts.newCodeReq = this._rows.filter((r) => r.mappingScore !== 100 && !r.notNeeded && !!r.reasonOfAdd).length
      this.counts.disableReq = this._rows.filter((r) => r.mappingScore !== 100 && r.notNeeded).length
      this.counts.actionNeeded = this._rows.length - this.counts.verified - this.counts.newCodeReq - this.counts.disableReq
   }

   getHierarchicalPracticeProducts(practiceSKey: number) {
      return this.productService.getHierarchicalPracticeProducts(practiceSKey)
   }

   onPIMSProductChanged($event: { pimsProduct: PIMSProductUIModel; newValue: number; missingProduct?: string; reasonOfAdd?: string }) {
      $event.pimsProduct.isSaving = true
      combineLatest([
         this.productService.getPracticeProducts($event.pimsProduct.practiceSKey),
         this.api.updateProduct({
            pimsProductSKey: $event.pimsProduct.productSKey,
            vcProductSKey: -1,
            missingProduct: $event.missingProduct,
            reasonOfAdd: $event.reasonOfAdd,
         }),
         $event.newValue > -1
            ? this.api.updateProduct({
                 pimsProductSKey: $event.newValue,
                 vcProductSKey: $event.pimsProduct.vcProductSKey,
                 missingProduct: $event.missingProduct,
                 reasonOfAdd: $event.reasonOfAdd,
              })
            : of({}),
      ])
         .pipe(
            take(1),
            finalize(() => {
               $event.pimsProduct.isSaving = false
            }),
            catchError((_) => {
               this.toastr.error('There was an issue saving.')
               $event.pimsProduct.savedSuccessfully = false
               return _
            }),
            tap(([allProds]) => {
               $event.pimsProduct.savedSuccessfully = true
               //$event.pimsProduct.mappingScore = 100;
               if (($event.newValue ?? -1) == -1) {
                  $event.pimsProduct.mappingScore = 99
                  $event.pimsProduct.vcProductUnlinked = true
               } else {
                  $event.pimsProduct.mappingScore = 100
                  $event.pimsProduct.vcProductUnlinked = false
                  const prod = allProds.find((p) => p.productSKey == $event.newValue)
                  if (prod) {
                     $event.pimsProduct.productSKey = prod?.productSKey
                     $event.pimsProduct.categoryName = prod?.categoryName
                     $event.pimsProduct.name = prod?.name
                     $event.pimsProduct.code = prod?.code
                     $event.pimsProduct.productType = prod?.productType
                  }
               }
            })
         )
         .subscribe()
   }

   onVcProductUnverify(pimsProduct: PIMSProductUIModel) {
      pimsProduct.isSaving = true
      this.api
         .unverifyProduct({
            pimsProductSKey: pimsProduct.productSKey,
            vcProductSKey: pimsProduct.vcProductSKey,
            missingProduct: undefined,
            reasonOfAdd: undefined,
         })
         .pipe(
            finalize(() => {
               pimsProduct.isSaving = false
            }),
            catchError((_) => {
               this.toastr.error('There was an issue saving.')
               pimsProduct.savedSuccessfully = false
               return _
            }),
            tap(() => {
               pimsProduct.mappingScore = 99
               pimsProduct.savedSuccessfully = true
            })
         )
         .subscribe(() => {
            this.calculateCounts() // kacy added
         })
   }

   onDoseSizeChange2(a, b) {
      console.log(a, b)
   }

   onDoseSizeChange($event: { pimsProduct: PIMSProductUIModel; doseSize: number }) {
      $event.pimsProduct.isSaving = true
      this.api
         .updateProductDoseSize({
            pimsProductSKey: $event.pimsProduct.productSKey,
            doseSize: $event.doseSize,
         })
         .pipe(
            take(1),
            finalize(() => {
               $event.pimsProduct.isSaving = false
            }),
            catchError((_) => {
               this.toastr.error('There was an issue saving.')
               $event.pimsProduct.savedSuccessfully = false
               return _
            }),
            tap(() => {
               $event.pimsProduct.doseSize = $event.doseSize
               $event.pimsProduct.savedSuccessfully = true
            })
         )
         .subscribe()
   }

   onVCProductChanged($event: {
      pimsProduct: PIMSProductUIModel
      newValue: number
      missingProduct?: string
      reasonOfAdd?: string
      notNeeded?: boolean
   }) {
      $event.pimsProduct.isSaving = true

      combineLatest([
         this.productService.allCategories$,
         this.allVCProducts$,
         this.api.updateProduct({
            pimsProductSKey: $event.pimsProduct.productSKey,
            vcProductSKey: $event.newValue ?? -1,
            missingProduct: $event.missingProduct,
            reasonOfAdd: $event.reasonOfAdd,
            notNeeded: $event.notNeeded,
         }),
      ])
         .pipe(
            take(1),
            finalize(() => {
               $event.pimsProduct.isSaving = false
            }),
            catchError((_) => {
               this.toastr.error('There was an issue saving.')
               $event.pimsProduct.savedSuccessfully = false
               return _
            }),
            tap(([vcCategories, allProducts, error]) => {
               const categoryLookup = {}
               vcCategories.forEach((cat) => (categoryLookup[cat?.vcProductCategorySKey] = cat))
               const vcProduct = allProducts.find((p) => p.vcProductSKey == $event.newValue)

               if (error) {
                  // $event.pimsProduct.savedSuccessfully = false;  // comment for 9-30 commit

                  if ($event.pimsProduct.vcProductUnlinked == true) {
                     //this.toastr.error(error);    // comment for 9-30 commit

                     // un-comment for 9-30 commit
                     this.api.updateProduct({
                        pimsProductSKey: $event.pimsProduct.productSKey,
                        vcProductSKey: vcProduct.vcProductSKey,
                        missingProduct: null,
                     })

                     this.onSameMap(
                        $event.pimsProduct,
                        vcProduct.vcProductSKey,
                        categoryLookup[vcProduct.vcProductCategorySKey].name,
                        vcProduct.vcProductCategorySKey,
                        vcProduct.name
                     ) // un-comment for 9-30 commit
                     //document.getElementById("relinkButton-"+this.clickedProdSKey+"").style.display="none";   // comment for 9-30 commit
                     //document.getElementById("clearAllButton-"+this.clickedProdSKey+"").style.cssText="width:auto; margin-right:2px; visibility:visible";	   // comment for 9-30 commit
                  } else {
                     //this.toastr.error(error);    // comment for 9-30 commit

                     // un-comment for 9-30 commit
                     this.api.updateProduct({
                        pimsProductSKey: $event.pimsProduct.productSKey,
                        vcProductSKey: vcProduct.vcProductSKey,
                        missingProduct: null,
                     })

                     this.onSameMap(
                        $event.pimsProduct,
                        vcProduct.vcProductSKey,
                        categoryLookup[vcProduct.vcProductCategorySKey].name,
                        vcProduct.vcProductCategorySKey,
                        vcProduct.name
                     ) // un-comment for 9-30 commit
                  }

                  //return;  // un-comment for 9-30 commit
               }

               $event.pimsProduct.savedSuccessfully = true

               //$event.pimsProduct.vcProductSKey = vcProduct?.vcProductSKey ?? -1;
               if (vcProduct) {
                  $event.pimsProduct.vcProduct = { ...vcProduct }
                  $event.pimsProduct.vcProductSKey = vcProduct.vcProductSKey
                  $event.pimsProduct.vcProduct.vcProductCategory = categoryLookup[vcProduct.vcProductCategorySKey]
               }
               //else {
               //   $event.pimsProduct.vcProduct = undefined;
               // }

               if (($event.newValue ?? -1) == -1) {
                  $event.pimsProduct.mappingScore = 99
                  $event.pimsProduct.vcProductUnlinked = true
                  $event.pimsProduct.notNeeded = $event.notNeeded
               } else {
                  $event.pimsProduct.mappingScore = 100
                  $event.pimsProduct.vcProductUnlinked = false
                  $event.pimsProduct.notNeeded = false
               }
            })
         )
         .subscribe(() => {
            this.calculateCounts()
         })
   }

   onRelink(product) {
      this.currentProduct = product
      this.onVCProductChanged({
         pimsProduct: this.currentProduct,
         newValue: this.currentProduct.vcProductSKey,
         missingProduct: this.missingProduct,
         reasonOfAdd: this.reasonOfAdd,
      })
   }

   onUnlink(product) {
      this.currentProduct = product
      this.missingProduct = product.name
      this.reasonOfAdd = undefined
      this.isThisCodeUsed = undefined
      this.unlinkDialogShown = true
   }

   doTheUnlink() {
      this.onVCProductChanged({
         pimsProduct: this.currentProduct,
         newValue: -1,
         missingProduct: this.missingProduct,
         reasonOfAdd: this.reasonOfAdd,
         notNeeded: !this.isThisCodeUsed,
      })

      const newRequest: AdminRequestDto = {
         productSKey: this.currentProduct.productSKey,
         practiceSKey: this.currentProduct.practiceSKey,
         supportNote: this.reasonOfAdd || '',
      }

      this.adminProductsClient.addRequest(newRequest).subscribe(
         () => {
            this.fetchNotesCounts([this.currentProduct.productSKey])

            this.toastr.success('Your request was successfully added and is being reviewed.')

            this.cancelUnlink()
         },
         (error) => {
            this.toastr.error('Failed to submit your request.')
         }
      )
   }

   cancelUnlink() {
      this.isThisCodeUsed = undefined
      this.unlinkDialogShown = false
      this.currentProduct = undefined
      this.missingProduct = undefined
      this.reasonOfAdd = undefined
      this.triedSearching = undefined
   }

   onSameMap(product, prodId, catName, catId, prodName) {
      this.currentProduct = product
      this.selectedVcId = prodId
      this.selectedVcCategory = catName
      this.selectedVcCategoryId = catId
      this.selectedVcProduct = prodName
      this.reasonSameMapping = undefined
      this.sameMapDialogShown = true
   }

   doTheSameMap() {
      const newRequest: AdminRequestDto = {
         productSKey: this.currentProduct.productSKey,
         practiceSKey: this.currentProduct.practiceSKey,
         supportNote:
            'REQUESTING MAPPING TO SAME PRODUCT: ' +
               this.selectedVcCategory +
               ' > ' +
               this.selectedVcProduct +
               ' - ' +
               this.selectedVcId +
               '<br><br>REASON: ' +
               this.reasonSameMapping || '',
      }

      this.adminProductsClient.addRequest(newRequest).subscribe(
         () => {
            //this.fetchNotesCounts([this.currentProduct.productSKey]);
            //this.toastr.success('Your mapping request was successfully added and is being reviewed.');
            //this.cancelSameMap();
         },
         (error) => {
            this.toastr.error('Failed to add the mapping request.')
         }
      )

      // go ahead and approve the same mapping request - un-comment below for 9-30 commit
      const requestData: VCLinkProductRequest = {
         newProductKey: this.selectedVcId,
         newProductCatId: this.selectedVcCategoryId,
         pimsProductId: this.currentProduct.productSKey,
      }
      this.adminProductsClient.vcLinkProduct(requestData).subscribe((success) => {
         if (success) {
            this.fetchNotesCounts([this.currentProduct.productSKey])

            this.toastr.success('Successfully linked the product.')

            this.cancelSameMap()
         } else {
            this.toastr.error('Failed to link the product.')
         }
      })
   }

   cancelSameMap() {
      this.sameMapDialogShown = false
      this.reasonSameMapping = undefined
   }

   /*scrolltop(whatscroll: string){
      //document.getElementById(whatscroll).scrollTop=0;
  }*/

   fetchSameMappedCounts(vcProductSKeyPairs: { vcProductSKey: number; practiceSKey: number }[]): void {
      vcProductSKeyPairs.forEach((pair) => {
         this.adminProductsClient.sameMappedCount(pair.vcProductSKey, pair.practiceSKey).subscribe((data) => {
            //const mappedCount = data.map(d => d.mappedCountNum);
            const productNames = data.map((d) => d.productName)
            this.sameMapCounts.set(pair.vcProductSKey, productNames)
         })
      })
   }

   public getSameMapCount(id: number): number {
      const productNames = this.sameMapCounts.get(id)
      return productNames ? productNames.length : 0
   }

   public getProductNamesTooltip(vcProductSKey: number, currentProductName: string): string {
      const productNames = this.sameMapCounts.get(vcProductSKey) || []
      const filteredNames = productNames.filter((name) => name !== currentProductName)
      return filteredNames.join(', ')
   }

   fetchNotesCounts(productSKeys: number[]): void {
      const BATCH_SIZE = 500 // Limit to stay below 2100 parameter limit (with a buffer)

      // Split the productSKeys into batches
      const batches = this.chunkArray(productSKeys, BATCH_SIZE)

      batches.forEach((batch) => {
         this.adminProductsClient.getProductMapNotesForIds(batch).subscribe(
            (data) => {
               batch.forEach((skey) => {
                  const notesForSkey = data.filter((note) => note.productSkey === skey)
                  const hasActionApproved = notesForSkey.some((note) => note.action === 3)
                  const count = hasActionApproved ? 99 : notesForSkey.length

                  const noteData = data.find((note) => note.productSkey === skey)
                  const theCount = noteData ? noteData.theCount : 0 // default to 0 if not found

                  this.productNotesCountMap.set(skey, count)
               })
            },
            (error) => {
               console.error('Error fetching record notes:', error)
            }
         )
      })
   }

   // Helper method to split array into chunks
   chunkArray(arr: number[], chunkSize: number): number[][] {
      const chunks: number[][] = []
      for (let i = 0; i < arr.length; i += chunkSize) {
         chunks.push(arr.slice(i, i + chunkSize))
      }
      return chunks
   }

   public getNotesCount(id: number): number {
      return this.productNotesCountMap.get(id) || 0
   }

   openNotesModal(id: number): void {
      if (this.getNotesCount(id) > 0) {
         this.showCommentLink = true
      } else {
         this.showCommentLink = false
      }
      setTimeout(() => document.getElementById('notesModalButton')?.click(), 100)
      this.loadingMapNotes = true
      this.selectedNoteProductSKey = id
      this.adminProductsClient.getProductMapNotes(id).subscribe(
         (data) => {
            this.productMapNotes = data.map((productMapNote) => ({ ...productMapNote }))
            this.loadingMapNotes = false
         },
         (error) => {
            console.error('Error fetching record details:', error)
         }
      )
   }

   toggleCommentBox() {
      this.showCommentBox = !this.showCommentBox
      //console.log('showCommentBox:', this.showCommentBox);
   }

   addSupportNote(): void {
      const newNote: ProductMapNoteDto = {
         productSkey: this.selectedNoteProductSKey,
         feedback: this.userFeedback,
         isAdmin: 0,
      }

      this.userFeedback = ''
      this.showCommentBox = false

      this.adminProductsClient.insertSupportNote(newNote).subscribe()
   }

   clickClearButton(product) {
      this.currentProduct = null
      product.vcProductSKey = undefined
      this.missingProduct = ''
   }
}
